/**
 * Stock History Page
 * View complete stock movement history with filters
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth, formatINR, formatIST } from '../lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { 
  History, Search, Filter, ArrowDown, ArrowUp, Package, 
  Truck, Undo2, Calendar, User, FileText, ChevronDown, ChevronUp, X
} from 'lucide-react';

const STOCK_CATEGORIES = ['Plywood', 'Blockboard', 'Doors', 'MDF', 'WPC/PVC'];

export default function StockHistoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  
  // Filters - initialise from URL params if present
  const [filterCategory, setFilterCategory] = useState(searchParams.get('category') || 'all');
  const [filterType, setFilterType] = useState('all');
  const [filterDays, setFilterDays] = useState('30');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('brand') || '');
  const [filterThickness, setFilterThickness] = useState(searchParams.get('thickness') || '');
  const [filterSize, setFilterSize] = useState(searchParams.get('size') || '');
  
  // Expanded items for details
  const [expandedItems, setExpandedItems] = useState({});

  // Check access
  const canAccess = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'stock_manager';

  useEffect(() => {
    if (user && !canAccess) {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, canAccess, navigate]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        days: parseInt(filterDays) || 30,
        limit: 200
      };
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterType !== 'all') params.change_type = filterType;
      if (searchQuery) params.brand_name = searchQuery;
      if (filterThickness) params.thickness = filterThickness;
      if (filterSize) params.size = filterSize;

      const res = await api.getStockHistory(params);
      setHistory(res.data.history || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterType, filterDays, searchQuery, filterThickness, filterSize]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.getStockHistorySummary(parseInt(filterDays) || 30);
      setSummary(res.data.summary || {});
    } catch (err) {
      logger.error('Failed to load summary');
    }
  }, [filterDays]);

  useEffect(() => {
    fetchHistory();
    fetchSummary();
  }, [fetchHistory, fetchSummary]);

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const changeTypeConfig = {
    delivery: { icon: Truck, color: 'text-red-600 bg-red-50', label: 'Delivery', prefix: '-' },
    revert: { icon: Undo2, color: 'text-green-600 bg-green-50', label: 'Revert', prefix: '+' },
    manual_add: { icon: ArrowUp, color: 'text-green-600 bg-green-50', label: 'Stock Added', prefix: '+' },
    manual_deduct: { icon: ArrowDown, color: 'text-red-600 bg-red-50', label: 'Stock Deducted', prefix: '-' },
    initial: { icon: Package, color: 'text-blue-600 bg-blue-50', label: 'Initial Stock', prefix: '+' }
  };

  const formatDate = (dateStr) => formatIST(dateStr);

  return (
    <div className="min-h-screen bg-[#F5F6F0] pb-24" data-testid="stock-history-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E2E6D9] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#556B2F]/10 flex items-center justify-center">
                <History className="w-5 h-5 text-[#556B2F]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#1A1C16]">Stock History</h1>
                <p className="text-xs text-[#8F9485]">View all stock movements and changes</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back to Stock
            </Button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-red-600" />
                <span className="text-xs text-red-600 font-medium">Deliveries</span>
              </div>
              <p className="text-lg font-bold text-red-700">
                -{Math.abs(summary.delivery?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-red-500">{summary.delivery?.count || 0} transactions</p>
            </div>
            
            <div className="bg-green-50 rounded-lg p-3 border border-green-100">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUp className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-600 font-medium">Stock Added</span>
              </div>
              <p className="text-lg font-bold text-green-700">
                +{Math.abs(summary.manual_add?.total_qty || 0) + Math.abs(summary.initial?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-green-500">
                {(summary.manual_add?.count || 0) + (summary.initial?.count || 0)} transactions
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Undo2 className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">Reverts</span>
              </div>
              <p className="text-lg font-bold text-blue-700">
                +{Math.abs(summary.revert?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-blue-500">{summary.revert?.count || 0} transactions</p>
            </div>
            
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-100">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDown className="w-4 h-4 text-yellow-600" />
                <span className="text-xs text-yellow-600 font-medium">Manual Deduct</span>
              </div>
              <p className="text-lg font-bold text-yellow-700">
                -{Math.abs(summary.manual_deduct?.total_qty || 0)}
              </p>
              <p className="text-[10px] text-yellow-500">{summary.manual_deduct?.count || 0} transactions</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8F9485]" />
              <Input
                placeholder="Search by brand..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#F9FAF7] border-[#E2E6D9] text-sm"
              />
            </div>
            
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[140px] bg-[#F9FAF7] border-[#E2E6D9] text-sm">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {STOCK_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] bg-[#F9FAF7] border-[#E2E6D9] text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
                <SelectItem value="revert">Revert</SelectItem>
                <SelectItem value="manual_add">Stock Added</SelectItem>
                <SelectItem value="manual_deduct">Stock Deducted</SelectItem>
                <SelectItem value="initial">Initial</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterDays} onValueChange={setFilterDays}>
              <SelectTrigger className="w-[120px] bg-[#F9FAF7] border-[#E2E6D9] text-sm">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Pre-filtered product context banner */}
      {(searchQuery || filterThickness || filterSize) && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-[#556B2F]/10 border border-[#556B2F]/30 rounded-lg p-3 flex items-center justify-between" data-testid="product-filter-banner">
            <div className="flex-1">
              <p className="text-xs text-[#5C6150] mb-1">Filtered to product</p>
              <p className="text-sm font-semibold text-[#556B2F]">
                {[searchQuery, filterThickness, filterSize].filter(Boolean).join(' • ')}
                {filterCategory !== 'all' && <span className="ml-2 text-xs font-normal">({filterCategory})</span>}
              </p>
              {(() => {
                const delivered = history.filter(h => h.change_type === 'delivery').reduce((s, h) => s + Math.abs(h.change_qty), 0);
                const added = history.filter(h => ['manual_add', 'initial'].includes(h.change_type)).reduce((s, h) => s + Math.abs(h.change_qty), 0);
                const customers = new Set(history.filter(h => h.customer_name).map(h => h.customer_name));
                return (
                  <p className="text-xs text-[#5C6150] mt-2">
                    <span className="inline-block mr-4"><strong className="text-red-600">-{delivered}</strong> delivered</span>
                    <span className="inline-block mr-4"><strong className="text-green-600">+{added}</strong> added</span>
                    <span className="inline-block"><strong>{customers.size}</strong> customer{customers.size === 1 ? '' : 's'}</span>
                  </p>
                );
              })()}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setFilterThickness('');
                setFilterSize('');
                setFilterCategory('all');
                setSearchParams({});
              }}
              data-testid="clear-product-filter-btn"
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* History List */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#556B2F] border-t-transparent rounded-full" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-[#8F9485] mx-auto mb-3" />
            <p className="text-[#5C6150]">No history found</p>
            <p className="text-sm text-[#8F9485]">Stock changes will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {history.map(item => {
              const config = changeTypeConfig[item.change_type] || changeTypeConfig.manual_add;
              const Icon = config.icon;
              const isExpanded = expandedItems[item.id];

              return (
                <div 
                  key={item.id} 
                  className="bg-white rounded-lg border border-[#E2E6D9] overflow-hidden"
                  data-testid={`history-item-${item.id}`}
                >
                  <button
                    onClick={() => toggleExpand(item.id)}
                    className="w-full p-3 flex items-center gap-3 hover:bg-[#F9FAF7] transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm text-[#1A1C16]">
                        {item.brand_name} - {item.thickness} - {item.size}
                      </p>
                      <p className="text-xs text-[#8F9485]">
                        {item.category} • {formatDate(item.created_at)}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className={`font-mono font-bold text-lg ${item.change_qty > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {item.change_qty > 0 ? '+' : ''}{item.change_qty}
                      </p>
                      <p className="text-[10px] text-[#8F9485]">
                        {item.previous_stock} → {item.new_stock}
                      </p>
                    </div>

                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-[#8F9485]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#8F9485]" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-[#E2E6D9] bg-[#F9FAF7]">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-[#8F9485]">Type:</span>
                          <Badge className={`ml-2 ${config.color}`}>{config.label}</Badge>
                        </div>
                        <div>
                          <span className="text-[#8F9485]">By:</span>
                          <span className="ml-2 font-medium">{item.user_name}</span>
                        </div>
                        {item.customer_name && (
                          <div className="col-span-2">
                            <span className="text-[#8F9485]">Customer:</span>
                            <span className="ml-2 font-medium">{item.customer_name}</span>
                          </div>
                        )}
                        {item.reference_number && (
                          <div className="col-span-2">
                            <span className="text-[#8F9485]">Reference:</span>
                            <span className="ml-2 font-mono text-[#556B2F]">{item.reference_number}</span>
                          </div>
                        )}
                        {item.notes && (
                          <div className="col-span-2">
                            <span className="text-[#8F9485]">Notes:</span>
                            <span className="ml-2">{item.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
